/**
 * StudySync Participant Verification Component
 * Secure verification flow for enrolled participants
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Security,
  CheckCircle,
  Email,
  Person,
  FitnessCenter,
  Warning,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ParticipantService } from '../../services/participantService';
import LoadingSpinner from '../common/LoadingSpinner';
import type { Participant } from '../../types';

const ParticipantVerification: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verified, setVerified] = useState(false);
  
  // Form data
  const [participantId, setParticipantId] = useState('');
  const [email, setEmail] = useState('');
  const [participantData, setParticipantData] = useState<Participant | null>(null);

  // Get data from navigation state if available
  useEffect(() => {
    if (location.state) {
      const { participantId: navParticipantId, email: navEmail } = location.state as any;
      if (navParticipantId) setParticipantId(navParticipantId);
      if (navEmail) setEmail(navEmail);
    }
  }, [location.state]);

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!participantId.trim() || !email.trim()) {
      setError('Please enter both Participant ID and email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await ParticipantService.verifyParticipant(
        participantId.trim(),
        email.trim().toLowerCase()
      );

      if (response.success && response.data) {
        setParticipantData(response.data);
        setVerified(true);
        setSuccess('Verification successful! Your enrollment is confirmed.');
      } else {
        setError(response.error || 'Verification failed. Please check your information and try again.');
      }
    } catch (err: any) {
      setError('Verification failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSecureVerification = async () => {
    if (!participantId.trim() || !email.trim()) {
      setError('Please enter both Participant ID and email address first');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await ParticipantService.initiateSecureVerification(
        participantId.trim(),
        email.trim().toLowerCase()
      );

      if (response.success) {
        setSuccess('Verification email sent! Please check your inbox and follow the instructions.');
      } else {
        setError(response.error || 'Failed to send verification email');
      }
    } catch (err: any) {
      setError('Failed to send verification email: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Verifying your information..." />;
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Security sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Participant Verification
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Verify your enrollment and check your study status
          </Typography>
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 4 }}>
          {!verified ? (
            <>
              {/* Verification Form */}
              <Typography variant="h6" sx={{ mb: 3 }}>
                Enter Your Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please enter your Participant ID and email address to verify your enrollment status.
              </Typography>

              <Box component="form" onSubmit={handleVerification}>
                <TextField
                  fullWidth
                  label="Participant ID"
                  value={participantId}
                  onChange={(e) => setParticipantId(e.target.value)}
                  placeholder="P1234567890ABCDEF"
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                  >
                    {loading ? 'Verifying...' : 'Verify Status'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={handleSecureVerification}
                    disabled={loading}
                  >
                    Send Verification Email
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Help Section */}
              <Typography variant="h6" sx={{ mb: 2 }}>
                Need Help?
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Person />
                  </ListItemIcon>
                  <ListItemText
                    primary="Participant ID"
                    secondary="Your unique 17-character ID starting with 'P' (provided after enrollment)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email Address"
                    secondary="The email address you used when enrolling in the study"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Security />
                  </ListItemIcon>
                  <ListItemText
                    primary="Secure Verification"
                    secondary="Use the email verification option for additional security"
                  />
                </ListItem>
              </List>
            </>
          ) : (
            <>
              {/* Verification Success */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Verification Successful!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your enrollment has been confirmed. Here's your current status:
                </Typography>
              </Box>

              {participantData && (
                <>
                  {/* Participant Information */}
                  <Card sx={{ mb: 3, backgroundColor: 'grey.50' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Your Study Information
                      </Typography>
                      <Box sx={{ display: 'grid', gap: 1 }}>
                        <Typography variant="body2">
                          <strong>Participant ID:</strong> {participantData.participantId}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Email:</strong> {participantData.participantEmail}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Study Code:</strong> {participantData.studyCode}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Status:</strong>{' '}
                          <Box
                            component="span"
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: participantData.status === 'active' ? 'success.light' : 'warning.light',
                              color: participantData.status === 'active' ? 'success.contrastText' : 'warning.contrastText',
                              fontSize: '0.75rem',
                            }}
                          >
                            {participantData.status.toUpperCase()}
                          </Box>
                        </Typography>
                        <Typography variant="body2">
                          <strong>Enrolled:</strong>{' '}
                          {participantData.createdAt ? new Date(participantData.createdAt).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Fitbit Status */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FitnessCenter sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6">
                          Fitbit Connection Status
                        </Typography>
                      </Box>
                      
                      {participantData.fitbitTokens?.accessToken ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                          <CheckCircle sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            Your Fitbit device is connected and sharing data
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                          <Warning sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            Fitbit device not connected. Please complete the Fitbit authorization process.
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Next Steps */}
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Next Steps
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Keep your Fitbit device active"
                            secondary="Wear your device daily to ensure continuous data collection"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <Email color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Check for study updates"
                            secondary="Researchers may send important updates via email"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <Security color="info" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Your data is secure"
                            secondary="All health data is encrypted and only accessible to authorized researchers"
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </>
              )}

              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/')}
                >
                  Return to Home
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ParticipantVerification;
